import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { getTour } from "../lib/tours";
import api from "../lib/api";
import { TourModal } from "./GuidedTourButton";

/**
 * Iter 43 — First-login welcome experience.
 *
 * On the FIRST login of a user (i.e. `user.welcome_tour_completed` is falsy),
 * we greet them with a personalised toast AND auto-open the persona-aware
 * guided tour for whichever page they land on (usually the dashboard).
 * When they finish/dismiss the tour, we POST /me/complete-welcome-tour so they
 * are never nagged again on subsequent logins.
 *
 * A `sessionStorage` guard prevents the tour from re-triggering on every
 * client-side route change within the same session; the backend flag is the
 * durable "never show again" signal.
 */
export default function WelcomeTourAutoLaunch() {
  const { user, refresh } = useAuth();
  const { lang } = useI18n();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const triggeredRef = useRef(false);

  useEffect(() => {
    if (!user) return;
    if (user.welcome_tour_completed) return;
    if (triggeredRef.current) return;
    if (sessionStorage.getItem("sw_welcome_shown") === "1") return;

    // Derive pageKey from current path — matches PageTour logic in Layout.jsx.
    const parts = loc.pathname.split("/").filter(Boolean);
    const pageKey = parts[0] === "app" ? (parts[1] || "dashboard") : null;
    if (!pageKey) return;

    const tour = getTour(pageKey, lang, user.role);
    if (!tour) return;

    triggeredRef.current = true;
    sessionStorage.setItem("sw_welcome_shown", "1");

    const firstName = (user.name || "").split(" ")[0] || (lang === "pt" ? "olá" : "there");
    const msg = lang === "pt"
      ? `Bem-vindo(a), ${firstName}! Aqui vai um tour rápido pra você começar.`
      : `Welcome, ${firstName}! Here's a quick tour to get you started.`;
    toast.success(msg, { duration: 5000, id: "welcome-toast" });
    setOpen(true);
  }, [user, lang, loc.pathname]);

  const handleClose = async () => {
    setOpen(false);
    try {
      await api.post("/me/complete-welcome-tour");
      // Refresh auth so `user.welcome_tour_completed` becomes true in memory
      // and no other page-level effects re-trigger the welcome flow.
      await refresh();
    } catch {
      // Silent — if the call fails, the sessionStorage guard still prevents
      // a re-open within this session; next login will retry.
    }
  };

  if (!user || user.welcome_tour_completed) return null;
  const parts = loc.pathname.split("/").filter(Boolean);
  const pageKey = parts[0] === "app" ? (parts[1] || "dashboard") : null;
  const tour = pageKey ? getTour(pageKey, lang, user.role) : null;
  if (!tour) return null;

  return (
    <TourModal
      open={open}
      onClose={handleClose}
      tour={tour}
      overline={lang === "pt" ? "Boas-vindas · Tour guiado" : "Welcome · Guided tour"}
    />
  );
}
